/**
 * Shared geometry for a dock's launcher and the panel it opens into.
 *
 * The two are never mounted at once — the dock shows one or the other — but
 * they must occupy the same place, or moving the launcher to the left of the
 * screen and opening it would throw the panel back to the right. So both read
 * and write ONE stored anchor, in launcher coordinates, and the panel converts
 * on the way in and back out.
 *
 * The alignment rule: the panel and the launcher share the corner nearest the
 * screen edge they are on. Bottoms always line up; the left edges line up on
 * the left half of the screen and the right edges on the right half. That way
 * the panel grows away from the edge rather than off it.
 */

/** Launcher diameter. The panel's conversion depends on it. */
export const BUBBLE_SIZE_PX = 56;

/** True when a box sits in the right half of the viewport. */
const isOnRightHalf = (left, width) =>
  left + width / 2 >= window.innerWidth / 2;

/**
 * Where the panel goes, given where the launcher is.
 *
 * @param {{left: number, top: number}} anchor - Launcher's top-left.
 * @param {number} panelWidth
 * @param {number} panelHeight
 * @returns {{left: number, top: number}} Panel's top-left.
 */
export function panelPositionFromAnchor(anchor, panelWidth, panelHeight) {
  const onRight = isOnRightHalf(anchor.left, BUBBLE_SIZE_PX);
  return {
    left: onRight ? anchor.left + BUBBLE_SIZE_PX - panelWidth : anchor.left,
    top: anchor.top + BUBBLE_SIZE_PX - panelHeight,
  };
}

/**
 * The inverse: where the launcher goes, given where the panel was dragged to.
 * Exact inverse of `panelPositionFromAnchor` for a given half of the screen, so
 * moving the panel and closing it leaves the launcher under your cursor.
 *
 * @param {{left: number, top: number}} position - Panel's top-left.
 * @param {number} panelWidth
 * @param {number} panelHeight
 * @returns {{left: number, top: number}} Launcher's top-left.
 */
export function anchorFromPanelPosition(position, panelWidth, panelHeight) {
  const onRight = isOnRightHalf(position.left, panelWidth);
  return {
    left: onRight ? position.left + panelWidth - BUBBLE_SIZE_PX : position.left,
    top: position.top + panelHeight - BUBBLE_SIZE_PX,
  };
}
